import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentryClient() {
  if (initialized) return;
  const env = import.meta.env as Record<string, string | undefined>;
  // Accept either PUBLIC_SENTRY_DSN (vite.config define) or VITE_SENTRY_DSN
  // (auto-exposed by Vite when set as an env var / build secret).
  const dsn = env.PUBLIC_SENTRY_DSN || env.VITE_SENTRY_DSN;
  if (!dsn) return;
  initialized = true;

  const environment = env.PUBLIC_SENTRY_ENVIRONMENT || env.VITE_SENTRY_ENVIRONMENT || "development";
  const release = env.PUBLIC_SENTRY_RELEASE || env.VITE_SENTRY_RELEASE || undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    // Replay is added lazily during idle time (see below) so its ~57 kB gzip
    // bundle never competes with the critical rendering path.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate: environment === "production" ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    // Hydration mismatches on the marketing site are overwhelmingly caused
    // by browser extensions (Grammarly, Dark Reader, password managers,
    // translators) that mutate the DOM before React hydrates. React 19
    // auto-recovers by re-rendering client-side, so these are noise.
    ignoreErrors: [
      /Hydration failed/i,
      /There was an error while hydrating/i,
      /Text content does not match server-rendered HTML/i,
      /Minified React error #(418|419|421|422|423|425)/,
    ],
    beforeSend(event, hint) {
      const err = hint?.originalException as { message?: string } | undefined;
      const msg = err?.message || event.message || "";
      if (/hydrat/i.test(msg) || /Minified React error #(418|419|421|422|423|425)/.test(msg)) {
        return null;
      }
      return event;
    },
  });

  // Session Replay: load + attach only once the browser is idle, so it never
  // blocks first paint / hydration. Error replays still work from that point on.
  const startReplay = () => {
    try {
      Sentry.addIntegration(Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }));
    } catch {
      /* noop */
    }
  };
  const idle = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
    }
  ).requestIdleCallback;
  if (idle) idle(startReplay, { timeout: 8000 });
  else window.setTimeout(startReplay, 4000);
}

export { Sentry };
